import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, FileText, Trash2, Sparkles, Save } from "lucide-react";
import Attachments from "@/components/Attachments";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [extracting, setExtracting] = useState(false);

  const load = () => api.get("/notes").then((r) => setNotes(r.data));
  useEffect(() => { load(); }, []);

  const select = (n) => { setActive(n); setTitle(n.title); setContent(n.content || ""); };

  const create = async () => {
    const { data } = await api.post("/notes", { title: "Untitled note", content: "" });
    await load();
    select(data);
  };

  const save = async () => {
    if (!active) return;
    await api.put(`/notes/${active.id}`, { title, content, tags: active.tags || [], project_id: active.project_id || null, linked_task_ids: active.linked_task_ids || [] });
    toast.success("Note saved");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/notes/${id}`);
    if (active?.id === id) { setActive(null); setTitle(""); setContent(""); }
    load();
  };

  const extract = async () => {
    if (!content.trim()) return;
    setExtracting(true);
    try {
      const { data } = await api.post("/agent/extract-tasks", { text: `${title}\n${content}`, source: "note" });
      const n = data.payload?.tasks?.length || 0;
      toast.success(data.status === "executed" ? `${n} tasks created` : `${n} tasks drafted — approve in Copilot`);
    } catch {
      toast.error("Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono text-slate-500">Knowledge</div>
          <h1 className="text-4xl font-black tracking-tighter">Notes</h1>
        </div>
        <Button onClick={create} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="new-note-button">
          <Plus size={16} className="mr-2" /> New Note
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="space-y-2 lg:col-span-1">
          {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          {notes.map((n) => (
            <Card key={n.id} onClick={() => select(n)}
              className={`rounded-sm shadow-none p-4 cursor-pointer transition-colors group ${active?.id === n.id ? "border-slate-950" : "border-border hover:border-slate-400"}`}
              data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between">
                <FileText size={16} className="text-slate-400 mt-0.5" />
                <button onClick={(e) => { e.stopPropagation(); remove(n.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive">
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="font-semibold text-sm mt-2 truncate">{n.title}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{n.content || "Empty"}</div>
              <div className="label-mono text-slate-400 mt-2">{new Date(n.updated_at).toLocaleDateString()}</div>
            </Card>
          ))}
        </div>

        <Card className="rounded-sm border-border shadow-none p-5 lg:col-span-2 min-h-[400px]">
          {!active ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              Select or create a note to start writing.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Input value={title} onChange={(e) => setTitle(e.target.value)}
                  className="rounded-sm text-xl font-bold border-0 px-0 focus-visible:ring-0" data-testid="note-title-input" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={extract} disabled={extracting}
                    className="rounded-sm bg-ai hover:bg-blue-800" data-testid="extract-tasks-button">
                    <Sparkles size={14} className="mr-1.5" /> {extracting ? "Extracting…" : "Tasks"}
                  </Button>
                  <Button size="sm" onClick={save} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="save-note-button">
                    <Save size={14} className="mr-1.5" /> Save
                  </Button>
                </div>
              </div>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)}
                placeholder="Start writing…" className="rounded-sm min-h-[340px] resize-none leading-relaxed"
                data-testid="note-content-input" />
              <div className="pt-3 border-t border-border">
                <Attachments parentType="note" parentId={active.id} />
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
